import React, { useState } from 'react';

const AdminNotificationSettings: React.FC = () => {
  const [values, setValues] = useState({
    smtpHost: 'smtp.gmail.com',
    smtpPort: 587,
    smtpUsername: '',
    smtpPassword: '',
    smtpUseTLS: true,
    fromEmail: 'noreply@bankingapp.com',
    fromName: 'Banking App',
    enableEmailNotifications: true,
    enableSystemAlerts: true,
    enableComplianceAlerts: true,
    enableSecurityAlerts: true,
    alertEmailRecipients: 'admin@bankingapp.com,security@bankingapp.com'
  });

  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const handleInputChange = (field: string, value: string | boolean | number) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage('');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSaveMessage('Notification settings saved successfully!');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (error) {
      setSaveMessage('Failed to save settings. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ 
      maxWidth: 800, 
      margin: '32px auto', 
      padding: '32px',
      background: '#ffffff',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      border: '1px solid #e5e7eb'
    }}>
      <div style={{ 
        marginBottom: '32px',
        paddingBottom: '24px',
        borderBottom: '2px solid #f3f4f6'
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          color: '#1f2937',
          fontSize: '28px',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <span style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            🔔 Notification Settings
          </span>
        </h2>
        <p style={{ 
          margin: 0, 
          color: '#6b7280', 
          fontSize: '16px',
          lineHeight: '1.5'
        }}>
          Configure email notifications, SMTP settings, and alert preferences
        </p>
      </div>

      {/* SMTP Configuration */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          📧 SMTP Configuration
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              SMTP Host
            </label>
            <input
              type="text"
              value={values.smtpHost}
              onChange={e => handleInputChange('smtpHost', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              SMTP Port
            </label>
            <input
              type="number"
              value={values.smtpPort}
              onChange={e => handleInputChange('smtpPort', Number(e.target.value))}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px',
          marginBottom: '20px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              SMTP Username
            </label>
            <input
              type="text"
              value={values.smtpUsername}
              onChange={e => handleInputChange('smtpUsername', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              SMTP Password
            </label>
            <input
              type="password"
              value={values.smtpPassword}
              onChange={e => handleInputChange('smtpPassword', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: '1fr 1fr', 
          gap: '20px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              From Email
            </label>
            <input
              type="email"
              value={values.fromEmail}
              onChange={e => handleInputChange('fromEmail', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px', 
              fontWeight: '600',
              color: '#374151',
              fontSize: '14px'
            }}>
              From Name
            </label>
            <input
              type="text"
              value={values.fromName}
              onChange={e => handleInputChange('fromName', e.target.value)}
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '16px',
                boxSizing: 'border-box'
              }}
            />
          </div>
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginTop: '20px',
          padding: '16px',
          background: '#ffffff',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <input
            type="checkbox"
            checked={values.smtpUseTLS}
            onChange={e => handleInputChange('smtpUseTLS', e.target.checked)}
            style={{ 
              width: '18px',
              height: '18px',
              accentColor: '#3b82f6'
            }}
          />
          <label style={{ 
            fontWeight: '500',
            color: '#374151',
            fontSize: '14px',
            margin: 0,
            cursor: 'pointer'
          }}>
            Use TLS Encryption
          </label>
        </div>
      </div>

      {/* Alert Settings */}
      <div style={{ 
        marginBottom: '40px',
        padding: '24px',
        background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
        borderRadius: '12px',
        border: '1px solid #fbbf24'
      }}>
        <h3 style={{ 
          margin: '0 0 20px 0', 
          color: '#1e293b',
          fontSize: '20px',
          fontWeight: '600',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🚨 Alert Settings
        </h3>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
          gap: '16px',
          marginBottom: '20px'
        }}>
          {[
            { key: 'enableEmailNotifications', label: 'Email Notifications' },
            { key: 'enableSystemAlerts', label: 'System Alerts' },
            { key: 'enableComplianceAlerts', label: 'Compliance Alerts' },
            { key: 'enableSecurityAlerts', label: 'Security Alerts' }
          ].map(item => (
            <div key={item.key} style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px',
              background: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #e5e7eb'
            }}>
              <input
                type="checkbox"
                checked={values[item.key as keyof typeof values] as boolean}
                onChange={e => handleInputChange(item.key, e.target.checked)}
                style={{ 
                  width: '18px',
                  height: '18px',
                  accentColor: '#f59e0b'
                }}
              />
              <label style={{ 
                fontWeight: '500',
                color: '#374151',
                fontSize: '14px',
                margin: 0,
                cursor: 'pointer'
              }}>
                {item.label}
              </label>
            </div>
          ))}
        </div>

        <div>
          <label style={{ 
            display: 'block', 
            marginBottom: '8px', 
            fontWeight: '600',
            color: '#374151',
            fontSize: '14px'
          }}>
            Alert Email Recipients
          </label>
          <input
            type="text"
            value={values.alertEmailRecipients}
            onChange={e => handleInputChange('alertEmailRecipients', e.target.value)}
            placeholder="admin@bankingapp.com,security@bankingapp.com"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '16px',
              boxSizing: 'border-box'
            }}
          />
          <div style={{ 
            fontSize: '13px', 
            color: '#6b7280', 
            marginTop: '8px'
          }}>
            Comma-separated list of email addresses to receive alerts
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div style={{ 
        textAlign: 'center', 
        marginTop: '32px',
        padding: '24px',
        background: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <button
          onClick={handleSave}
          disabled={isSaving}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '16px 32px',
            borderRadius: '8px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: isSaving ? 'not-allowed' : 'pointer',
            opacity: isSaving ? 0.7 : 1,
            transition: 'all 0.2s ease',
            boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isSaving ? (
            <>
              <div style={{
                width: '16px',
                height: '16px',
                border: '2px solid #ffffff',
                borderTop: '2px solid transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              Saving...
            </>
          ) : (
            <>
              💾 Save Notification Settings
            </>
          )}
        </button>
        
        {saveMessage && (
          <div style={{
            marginTop: '16px',
            padding: '12px 16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
            color: saveMessage.includes('successfully') ? '#065f46' : '#991b1b',
            background: saveMessage.includes('successfully') ? '#d1fae5' : '#fee2e2',
            border: `1px solid ${saveMessage.includes('successfully') ? '#a7f3d0' : '#fecaca'}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px'
          }}>
            {saveMessage.includes('successfully') ? '✅' : '❌'} {saveMessage}
          </div>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminNotificationSettings;
